// What the user did: clicks, changed form fields and form submissions.
// Captured in the capture phase so pages that stop propagation are still seen.

import { truncate } from '../../shared/serialise.js';

/** `button#book.btn "Book now"` */
export function describeElement(el) {
  if (!el || el.nodeType !== 1) return 'element';
  // getAttribute, not el.id / el.className: a form with <input name="id"> "clobbers" form.id.
  let out = el.tagName.toLowerCase();
  const id = el.getAttribute('id');
  if (id) out += `#${id}`;
  const classes = (el.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean);
  if (classes.length) out += `.${classes.slice(0, 2).join('.')}`;
  const name = el.getAttribute('name');
  if (name) out += `[name="${name}"]`;
  const label = el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('alt');
  const ownText = typeof el.innerText === 'string' ? el.innerText : '';
  const value = typeof el.value === 'string' ? el.value : '';
  const text = (label || ownText || value).replace(/\s+/g, ' ').trim();
  if (text && !['input', 'textarea', 'select'].includes(el.tagName.toLowerCase())) {
    out += ` "${truncate(text, 40)}"`;
  }
  return out;
}

function absolute(url) {
  try {
    return new URL(url, location.href).href;
  } catch {
    return String(url);
  }
}

/** A short CSS path, good enough to find the element again. */
export function cssPath(el) {
  const parts = [];
  let node = el;
  while (node && node.nodeType === 1 && parts.length < 5) {
    const id = node.getAttribute('id');
    if (id) {
      parts.unshift(`#${CSS.escape(id)}`);
      break;
    }
    let part = node.tagName.toLowerCase();
    const parent = node.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
      if (siblings.length > 1) part += `:nth-of-type(${siblings.indexOf(node) + 1})`;
    }
    parts.unshift(part);
    node = parent;
  }
  return parts.join(' > ');
}

/** The element the user actually interacted with (clicks on text nodes, SVG paths in buttons). */
function interactiveTarget(event) {
  let el = event.target;
  if (el && el.nodeType !== 1) el = el.parentElement;
  if (!el || typeof el.closest !== 'function') return el;
  return el.closest('a, button, input, select, textarea, label, summary, [role="button"], [onclick]') || el;
}

export function installActions(ctx, doc = document) {
  const maskAll = ctx.config.maskInputs;

  doc.addEventListener(
    'click',
    (event) => {
      try {
        if (ctx.isOwnEvent(event)) return;
        const el = interactiveTarget(event);
        if (!el) return;
        ctx.push('click', 'info', `Clicked ${describeElement(el)}`, {
          selector: cssPath(el),
          href:
            el.tagName === 'A' && el.getAttribute('href')
              ? ctx.redact.url(absolute(el.getAttribute('href')))
              : undefined,
          x: Math.round(event.clientX),
          y: Math.round(event.clientY),
        });
      } catch {
        // ignore
      }
    },
    true,
  );

  doc.addEventListener(
    'change',
    (event) => {
      try {
        if (ctx.isOwnEvent(event)) return;
        const el = event.target;
        if (!el || el.nodeType !== 1) return;
        const type = (el.getAttribute('type') || el.tagName).toLowerCase();
        let value;
        if (type === 'password') value = '[password]';
        else if (type === 'checkbox' || type === 'radio') value = el.checked;
        else if (type === 'file') value = Array.from(el.files || [], (f) => f.name);
        else if (maskAll) value = '[masked]';
        else if (ctx.redact.isKey(el.getAttribute('name')) || ctx.redact.isKey(el.getAttribute('id')))
          value = '[redacted]';
        else if (el.tagName === 'SELECT' && el.multiple) value = Array.from(el.selectedOptions, (o) => o.value);
        else value = truncate(String(el.value ?? ''), 500);
        ctx.push('input', 'info', `Changed ${describeElement(el)}`, {
          selector: cssPath(el),
          type,
          value,
        });
      } catch {
        // ignore
      }
    },
    true,
  );

  doc.addEventListener(
    'submit',
    (event) => {
      try {
        if (ctx.isOwnEvent(event)) return;
        const form = event.target;
        ctx.push('submit', 'info', `Submitted ${describeElement(form)}`, {
          selector: cssPath(form),
          // getAttribute: <input name="action"> or name="method" clobbers form.action / form.method.
          action: form.getAttribute('action') ? ctx.redact.url(absolute(form.getAttribute('action'))) : undefined,
          method: (form.getAttribute('method') || 'get').toUpperCase(),
        });
      } catch {
        // ignore
      }
    },
    true,
  );
}
